import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Form, InputField, PageShell } from 'components';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  RootState,
  updateClientById,
  fetchClientById,
} from 'data';
import { useFormChanges } from 'hooks';
import { useTranslation } from 'react-i18next';
import { countries } from 'helpers';

export const EditClient: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [submitted, setSubmitted] = useState(false);

  const { selectedClient, getClientsLoading, updatingClientLoading } =
    useSelector((state: RootState) => state.clients);

  useEffect(() => {
    if (id) {
      dispatch(fetchClientById(Number(id)));
    }
  }, [dispatch, id]);

  const [formData, setFormData] = useState({
    id: 0,
    name: '',
    contactPersonFirstName: '',
    contactPersonLastName: '',
    contactPersonSalutation: '',
    email: '',
    phone: '',
    website: '',
    created_at: '',
    updated_at: '',
    address: {
      id: undefined as number | undefined,
      Street: '',
      HouseNumber: '',
      PostCode: '',
      City: '',
      Country: '',
    },
  });

  const initialFormDataRef = useRef(formData);

  useEffect(() => {
    if (selectedClient) {
      const newFormData = {
        id: selectedClient.id,
        name: selectedClient.name,
		contactPersonFirstName: selectedClient.contactPersonFirstName || '',
		contactPersonLastName: selectedClient.contactPersonLastName || '',
		contactPersonSalutation: selectedClient.contactPersonSalutation || '',
        email: selectedClient.email || '',
        phone: selectedClient.phone || '',
        website: selectedClient.website || '',
        created_at: selectedClient.created_at,
        updated_at: selectedClient.updated_at,
        address: selectedClient.address
          ? {
              id: selectedClient.address.id ?? undefined,
              Street: selectedClient.address.Street || '',
              HouseNumber: selectedClient.address.HouseNumber || '',
              PostCode: selectedClient.address.PostCode || '',
              City: selectedClient.address.City || '',
              Country: selectedClient.address.Country || '',
            }
          : {
              id: undefined,
              Street: '',
              HouseNumber: '',
              PostCode: '',
              City: '',
              Country: '',
            },
      };

      setFormData(newFormData);
      initialFormDataRef.current = newFormData;
    }
  }, [selectedClient]);

  const isFormChanged = useFormChanges(initialFormDataRef.current, formData);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (
      ['Street', 'HouseNumber', 'PostCode', 'City', 'Country'].includes(name)
    ) {
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [name]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
	setSubmitted(true);
	if (formData.name == '' || 
		formData.address.Street == '' ||
		formData.address.HouseNumber == '' ||
		formData.address.PostCode == '' ||
		formData.address.City == '' ||
		formData.address.Country == '' ||
		formData.contactPersonFirstName == '' ||
		formData.contactPersonLastName == '' ||
		formData.contactPersonSalutation == '' ||
		formData.email == '' ||
		formData.phone == '') {
		return
	}
    try {
      await dispatch(updateClientById(formData)).unwrap();
      navigate('/clients');
    } catch (err) {
      console.error(t('editClient.toast.failedToUpdateClient'), err);
    }
  };

	if (getClientsLoading) {
		return <p>{t('editClient.loadingClientData')}</p>;
	}

	const nationalityOptions = [
		{ label: t('personalIdentification.select'), value: '' },
		...countries.map((country) => ({
		label: t(country.labelKey),
		value: country.value,
		})),
	];

  	const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

  return (
    <PageShell width="narrow">
      <h1 className="text-2xl lg:text-3xl font-medium text-[#2E435F]">
        {t('editClient.editClient')}
      </h1>
      <Form
        content={
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <InputField
              label={t('editClient.companyName')}
              name="name"
              value={formData.name}
              onChange={handleChange}
			  error={submitted && !formData.name ? 'Required': ''}
            />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
				<Dropdown
					label={t('addNewClient.contactPersonSalutation')}
					name="contactPersonSalutation"
					value={formData.contactPersonSalutation || ''}
					options={[
						{ label: t('personalIdentification.select'), value: '' },
						{
							label: t('personalIdentification.mr'),
							value: 'mr',
						},
						{
							label: t('personalIdentification.mrs'),
							value: 'mrs',
						}
					]}
					onChange={handleDropdownChange}
					error={submitted && !formData.contactPersonSalutation ? 'Required': ''}
				/>
				<InputField
					label={t('addNewClient.contactPersonFirstName')}
					name="contactPersonFirstName"
					value={formData.contactPersonFirstName}
					onChange={handleChange}
					placeholder={t('addNewClient.contactPersonFirstName')}
					error={submitted && !formData.contactPersonFirstName ? 'Required': ''}
				/>
				<InputField
					label={t('addNewClient.contactPersonLastName')}
					name="contactPersonLastName"
					value={formData.contactPersonLastName}
					onChange={handleChange}
					placeholder={t('addNewClient.contactPersonLastName')}
					error={submitted && !formData.contactPersonLastName ? 'Required': ''}
				/>
			</div>
            <InputField
              label={t('editClient.email')}
              name="email"
              value={formData.email}
              onChange={handleChange}
			  error={submitted && !formData.email ? 'Required': ''}
            />
            <InputField
              label={t('editClient.phoneNumber')}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
			  error={submitted && !formData.phone ? 'Required': ''}
            />
            <InputField
              label={t('editClient.website')}
              name="website"
              value={formData.website}
              onChange={handleChange}
            />

            <h2 className="text-lg font-semibold text-[#2E435F]">
              {t('editClient.address')}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <InputField
                label={t('editClient.street')}
                name="Street"
                value={formData.address.Street}
                onChange={handleChange}
				error={submitted && !formData.address.Street ? 'Required': ''}
              />
              <InputField
                label={t('editClient.houseNumber')}
                name="HouseNumber"
                value={formData.address.HouseNumber}
                onChange={handleChange}
				error={submitted && !formData.address.HouseNumber ? 'Required': ''}
              />
            </div>
            <InputField
              label={t('editClient.postcode')}
              name="PostCode"
              value={formData.address.PostCode}
              onChange={handleChange}
			  error={submitted && !formData.address.PostCode ? 'Required': ''}
            />
            <InputField
              label={t('editClient.city')}
              name="City"
              value={formData.address.City}
              onChange={handleChange}
			  error={submitted && !formData.address.City ? 'Required': ''}
            />
            <Dropdown
              label={t('editClient.country')}
              name="Country"
              value={formData.address.Country}
              options={nationalityOptions}
              onChange={handleChange}
			  error={submitted && !formData.address.Country ? 'Required': ''}
            />
            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-[16px] mt-6">
              <Button
                label={t('editClient.cancel')}
                onClick={() => navigate('/clients')} 
                color="#828E9F"
                fullWidth
              />
              <Button
                label={
                  updatingClientLoading
                    ? t('editClient.editing')
                    : t('editClient.updateClient')
                }
                type="submit"
                loading={updatingClientLoading}
                disabled={updatingClientLoading || !isFormChanged}
                style={{
                  opacity: updatingClientLoading || !isFormChanged ? 0.5 : 1,
                }}
              />
            </div>
          </form>
        }
      />
    </PageShell>
  );
};
